import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Store, Building2, CalendarDays, Hammer, MapPin, Loader2, Navigation, Map as MapIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RADII = [
  { m: 500, l: '500 m' },
  { m: 2000, l: '2 km' },
  { m: 5000, l: '5 km' },
  { m: 10000, l: '10 km' },
];

const CAT = {
  shop: { lv: 'Veikals', en: 'Shop', ru: 'Магазин', color: '#2563EB', Icon: Store },
  business: { lv: 'Uzņēmums', en: 'Business', ru: 'Бизнес', color: '#7C3AED', Icon: Building2 },
  event: { lv: 'Pasākums', en: 'Event', ru: 'Событие', color: '#E11D48', Icon: CalendarDays },
  gig: { lv: 'Haltūra', en: 'Gig', ru: 'Подработка', color: '#D97736', Icon: Hammer },
};

const fmtDist = (m) => (m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`);

// "Tavā tuvumā" — rendered as a block BELOW the map (like the business / event
// result lists), a responsive card grid sorted by distance.
export default function NearbyPanel({ open, onClose, userLoc, onOpenOnMap }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);
  const [radius, setRadius] = useState(5000);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const sectionRef = useRef(null);

  // Fetch nearby items; abort + ignore stale responses so rapid radius taps
  // never render results for a radius the user has since changed away from.
  useEffect(() => {
    if (!open || !userLoc) return undefined;
    const controller = new AbortController();
    let ignore = false;
    setLoading(true);
    axios.get(`${API}/map/nearby`, {
      params: { lat: userLoc.lat, lng: userLoc.lng, radius, limit: 80 },
      signal: controller.signal,
    })
      .then(({ data }) => { if (!ignore) setItems(data.items || []); })
      .catch((err) => { if (!ignore && !axios.isCancel(err)) setItems([]); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; controller.abort(); };
  }, [open, userLoc, radius]);

  // Scroll the results into view when they first open (helps on the full-height
  // /karte map where the list sits below the fold).
  useEffect(() => {
    if (open && userLoc && sectionRef.current) {
      const id = setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
      return () => clearTimeout(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userLoc]);

  if (!open || !userLoc) return null;

  return (
    <section ref={sectionRef} className="mt-4 scroll-mt-20" data-testid="nearby-panel">
      <div className="bg-white rounded-2xl border border-[#E5E4E0] p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Navigation className="w-4 h-4 text-[#2563EB] shrink-0" />
            <h3 className="font-outfit text-base font-semibold text-[#121212] truncate">
              {tr('Tavā tuvumā', 'Near you', 'Рядом с вами')}
            </h3>
            {!loading && <span className="text-sm text-[#9CA3AF] shrink-0">({items.length})</span>}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#F3F2EE] text-[#5C5C5C] shrink-0" data-testid="nearby-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="nearby-radius">
          {RADII.map((r) => (
            <button key={r.m} type="button" onClick={() => setRadius(r.m)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${radius === r.m ? 'bg-[#2563EB] text-white border-transparent' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
              data-testid={`nearby-radius-${r.m}`}>
              {r.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#9CA3AF]">
            <Loader2 className="w-5 h-5 animate-spin text-[#2563EB]" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#9CA3AF] text-center py-10" data-testid="nearby-empty">
            {tr('Šajā rādiusā nekas nav atrasts. Pamēģini lielāku attālumu.', 'Nothing found in this radius. Try a larger distance.', 'В этом радиусе ничего не найдено. Попробуйте больше.')}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="nearby-list">
            {items.map((it, i) => {
              const meta = CAT[it.category] || CAT.shop;
              const Icon = meta.Icon;
              return (
                <div key={`${it.category}-${it.id}-${i}`}
                  onClick={() => it.link && navigate(it.link)}
                  className="bg-white rounded-2xl border border-[#F0EFEB] p-3 flex items-center gap-3 text-left cursor-pointer hover:border-[#2563EB] hover:shadow-md transition-all"
                  data-testid={`nearby-item-${i}`}>
                  {it.thumbnail ? (
                    <img src={it.thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                  ) : (
                    <span className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}1A` }}>
                      <Icon className="w-6 h-6" style={{ color: meta.color }} />
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ backgroundColor: `${meta.color}1A`, color: meta.color }}>
                        {tr(meta.lv, meta.en, meta.ru)}
                      </span>
                      {it.budget != null && <span className="text-[11px] font-semibold text-[#D97736]">€{it.budget}</span>}
                    </div>
                    <p className="text-sm font-semibold text-[#121212] truncate">{it.name}</p>
                    <p className="flex items-center gap-1 text-[11px] text-[#9CA3AF] truncate">
                      <MapPin className="w-3 h-3 shrink-0" /> {it.subtitle || ''}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="text-xs font-semibold text-[#2563EB]">{fmtDist(it.distance_m)}</span>
                    {onOpenOnMap && it.lat != null && (
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenOnMap(it); }}
                        className="flex items-center gap-1 text-[11px] font-semibold text-[#5C5C5C] hover:text-[#2563EB] px-2 py-1 rounded-full border border-[#E5E4E0] hover:border-[#2563EB] transition-colors"
                        title={tr('Rādīt kartē', 'Show on map', 'Показать на карте')}
                        data-testid={`nearby-map-${i}`}>
                        <MapIcon className="w-3.5 h-3.5" /> {tr('Kartē', 'Map', 'Карта')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
